import React, { useEffect, useMemo, useState, useContext } from "react";

import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";
import ImageWithButton from "./ImageWithButton";
import ImageWithText from "./ImageWithText";

const RowContainer = styled(motion.div)`
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
  justify-content: center;
`;

const ImageContainer = styled(motion.div)`
  width: ${(props) => props.imageWidth};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ShowMoreButton = styled.button`
  display: block;
  margin: 2rem auto;
  padding: 0.75rem 2rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
  width: fit-content;

  &:hover {
    background-color: #0056b3;
  }
`;

function PhotoRow() {
  const {
    imagesInRow = "four",
    animate = false,
    animateOnce = true,
    animatedItems = [],
    visibleCards = 12,
    type,
  } = useContext(ComponentContext);

  const [displayedItems, setDisplayedItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: animateOnce,
    threshold: 0.2,
  });

  useEffect(() => {
    setDisplayedItems(animatedItems.slice(0, visibleCards));
  }, [animatedItems, visibleCards]);

  const toggleShowMore = () => {
    if (showAll) {
      setDisplayedItems(animatedItems.slice(0, visibleCards));
    } else {
      setDisplayedItems(animatedItems);
    }
    setShowAll(!showAll);
  };

  const imagesRowMap = {
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
  };

  const columnValue = useMemo(
    () => 12 / imagesRowMap[imagesInRow],
    [imagesInRow]
  );

  let Component;

  switch (type) {
    case "withButton": {
      Component = ImageWithButton;
      break;
    }
    default: {
      Component = ImageWithText;
      break;
    }
  }

  useEffect(() => {
    if (animate) {
      if (inView) {
        controls.start("visible");
        setHasAnimated(true);
      } else if (!animateOnce && hasAnimated) {
        controls.start("hidden");
        setHasAnimated(false);
      }
    } else {
      controls.set("visible");
    }
  }, [controls, inView, hasAnimated, animate, animateOnce]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -30,
      transition: { duration: 0.3 },
    },
  };

  return (
    <>
      <RowContainer
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={controls}
        className="row gx-4 gy-4 photo-row"
      >
        <AnimatePresence>
          {displayedItems.map((photo, index) => (
            <ImageContainer
              key={photo?.id ?? `photo-${index}`}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={`col-lg-${columnValue} col-md-4 col-sm-6 col-12 position-relative image-container`}
            >
              <Component
                photoUrl={photo?.image?.url}
                id={`${index}`}
                initialText={photo.initialText}
                hoverText={photo.secondaryText}
                objectFit={photo.objectFit}
                buttonUrl={photo.buttonURL}
                buttonText={photo.buttonText}
                offset={photo.offset}
                hasOverlayOnHover={photo.hasOverlayOnHover}
                popupText={photo.popupText}
              />
              {photo.textAfterImage && (
                <div className="text-after-image">
                  <Markdown>{photo.textAfterImage}</Markdown>
                </div>
              )}
            </ImageContainer>
          ))}
        </AnimatePresence>
      </RowContainer>
      {visibleCards && animatedItems.length > visibleCards && (
        <ShowMoreButton onClick={toggleShowMore} className="show-more-button">
          {showAll ? "Show Less" : "Show More"}
        </ShowMoreButton>
      )}
    </>
  );
}

export default PhotoRow;
